import { inject } from 'mobx-react';
import * as React from 'react';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { PropertyNavigationBar } from '@shared-ui/lodging-property-details';
import { RefTarget, useRefContext } from '@shared-ui/ref-context';
import { ViewLarge, Viewport } from '@shared-ui/viewport-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { MultiItemStore } from 'stores/multi-item-store';
import { useRefProvider } from 'src/common/stores/contexts/ref-provider';

// UITK
import { UitkSticky } from 'uitk-react-sticky';
import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkSpacing } from 'uitk-react-spacing';

// Components
import { SharedUiWrapperProps, withSharedUiPropertyDetails } from './shared-ui-property-details';
import { SharedUiStickyPriceHeaderWrapper } from '../offers/shared-ui-property-sticky-price-header-wrapper';
import { StickyBookBar } from 'components/infosite/template-components/sticky-book-bar';
import ViewportListener from 'components/common/lazy/viewport-listener';

interface NavbarWrapperProps extends SharedUiWrapperProps {
  showReserveBtn?: boolean;
  showPriceOnLoad?: boolean;
  showNightlyPrice?: boolean;
  showReserveBtnOnLoad?: boolean;
  currentHotel?: CurrentHotelStore;
  multiItem?: MultiItemStore;
}

const NavigationBarWrapper: React.FunctionComponent<NavbarWrapperProps> = inject(
  'currentHotel',
  'multiItem'
)(({ inputs, showReserveBtn, showPriceOnLoad = false, showNightlyPrice, showReserveBtnOnLoad, currentHotel, multiItem }) => {
  const [priceboxInView, setPriceboxInView] = React.useState(!showPriceOnLoad);
  const [reserveBtnInView, setReserveBtnInView] = React.useState(showReserveBtnOnLoad);
  const [singleOfferRef] = useRefProvider('singleOfferPrice');

  const { getTargets } = useRefContext();
  const { Offers: offersRef } = getTargets(['Offers']);

  const singleOfferRefCurrent = singleOfferRef?.current;
  const offersRefCurrent = offersRef?.current;
  const searchCriteria = currentHotel?.searchCriteria;
  const isDateLess = searchCriteria?.isDateless;
  const isMultiItem = multiItem?.cartInfo?.id ?? multiItem?.isPackageShoppingPath();

  const viewportListener = new ViewportListener();

  React.useEffect(() => {
    if (singleOfferRefCurrent) {
      viewportListener.subscribe('window', singleOfferRefCurrent, {
        didEnter: () => setPriceboxInView(true),
        didExit: () => setPriceboxInView(false),
      });
    }
  }, [singleOfferRefCurrent]);

  React.useEffect(() => {
    if (offersRefCurrent) {
      new IntersectionObserver((entries) => entries.forEach((entry) => setReserveBtnInView(!entry.isIntersecting)), {
        rootMargin: '0% 0% -50% 0%',
      }).observe(offersRefCurrent);
    }
  }, [offersRefCurrent]);

  return (
    <UitkSticky zIndex={400}>
      <RefTarget name="navigation">
        <>
          <UitkCard className="uitk-flat-border-top uitk-flat-border-bottom">
            <UitkCardContentSection border="bottom" padded={false} data-stid="uitk-card-content-section-navbar">
              <PropertyNavigationBar inputs={inputs} />
            </UitkCardContentSection>
          </UitkCard>
          {((showReserveBtn && !priceboxInView) || (!showNightlyPrice && reserveBtnInView)) && (
            <Viewport>
              <ViewLarge>
                <UitkLayoutPosition type="absolute" position={{ right: 'zero', top: 'zero' }}>
                  <UitkSpacing padding={{ blockstart: 'one' }}>
                    <UitkLayoutFlex space="two" justifyContent="end" alignItems="center">
                      <UitkLayoutFlexItem grow={0}>
                        <UitkSpacing padding={{ inlineend: 'three' }}>
                          <div data-stid="navbar-quick-reserve-button">
                            {isDateLess || isMultiItem ? (
                              <StickyBookBar />
                            ) : (
                              <Experiment name="Price_Summary_above_the_scroll_line_on_PDP">
                                <ExperimentControl>
                                  <StickyBookBar />
                                </ExperimentControl>
                                <ExperimentVariant bucket={1}>
                                  <SharedUiStickyPriceHeaderWrapper hidePrice={true} />
                                </ExperimentVariant>
                                <ExperimentVariant bucket={2} />
                                <ExperimentVariant bucket={3}>
                                  <SharedUiStickyPriceHeaderWrapper hideCtaButton={true} />
                                </ExperimentVariant>
                              </Experiment>
                            )}
                          </div>
                        </UitkSpacing>
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                  </UitkSpacing>
                </UitkLayoutPosition>
              </ViewLarge>
            </Viewport>
          )}
        </>
      </RefTarget>
    </UitkSticky>
  );
});

export const SharedUiNavigationBarWrapper = withSharedUiPropertyDetails<Omit<NavbarWrapperProps, 'inputs'>>(NavigationBarWrapper);
SharedUiNavigationBarWrapper.displayName = 'SharedUiNavigationBarWrapper';
